import { useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { ModalShellHeader } from "../../../components/modal/ModalShellHeader.jsx";
import { buildModalShellTokens } from "../../../components/modal/modalShellTheme.js";
import { DeleteConfirmModal } from "../../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm.js";
import { buildAccountDetailTokens, detailActionBtnStyle } from "../accountDetailTheme.js";
import {
  BAUD_OPTIONS,
  CMD_COUNT_OPTIONS,
  MODEL_OPTIONS,
  buildCommandDetailFields,
  getCommandTypeLabel,
  packetFromSavedCommand,
} from "../commandUtils.js";
import {
  CommandSlotEditor,
  FieldLabel,
  SectionCard,
  SectionHeader,
  Segmented,
  buildCommandTheme,
} from "./commandBuilderControls.jsx";
import { CommandPreviewPanel } from "./CommandPreviewPanel.jsx";

const TYPE_ICONS = {
  can: Ico.chip,
  "device-pin": Ico.device,
  custom: Ico.terminal,
};

function ReadOnlyInput({ value, c, theme, icon, placeholder = "—" }) {
  return (
    <div style={{ position: "relative" }}>
      {icon && (
        <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: c.sub, pointerEvents: "none", display: "flex" }}>
          {icon}
        </span>
      )}
      <div
        style={{
          ...theme.input,
          paddingLeft: icon ? 34 : 11,
          display: "flex",
          alignItems: "center",
          cursor: "default",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || placeholder}
      </div>
    </div>
  );
}

function ReadOnlyTextarea({ value, c, theme, placeholder = "—" }) {
  return (
    <div
      style={{
        ...theme.input,
        height: "auto",
        minHeight: 72,
        padding: "10px 11px",
        cursor: "default",
        lineHeight: 1.45,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value || placeholder}
    </div>
  );
}

const VIEW_FRAME_SLOT = {
  arbId: "",
  extended: true,
  dlc: 0,
  data: "",
  timeoutMs: "",
  count: "",
};

function CanCommandViewBody({ command, dark, c, A, theme }) {
  const { input } = theme;
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useLayoutEffect(() => {
    setPreviewExpanded(false);
    setCopied(false);
  }, [command?.id]);

  const packet = useMemo(() => packetFromSavedCommand(command), [command]);
  const modelLabel = command.model ?? MODEL_OPTIONS[0].label;

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(packet.rawHex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionCard theme={theme}>
        <SectionHeader title="General" hint="Model, name, description, and bus parameters." icon={Ico.chip} A={A} c={c} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <FieldLabel c={c}>Model</FieldLabel>
            <div style={{ ...input, cursor: "default", display: "flex", alignItems: "center" }}>{modelLabel}</div>
          </div>
          <div style={{ minWidth: 0 }}>
            <FieldLabel c={c}>Command name</FieldLabel>
            <ReadOnlyInput value={command.name} c={c} theme={theme} icon={Ico.terminal} />
          </div>
          <div style={{ minWidth: 0, gridColumn: "1 / -1" }}>
            <FieldLabel c={c}>Description</FieldLabel>
            <ReadOnlyTextarea value={command.description} c={c} theme={theme} />
          </div>
          <div style={{ minWidth: 0 }}>
            <FieldLabel c={c}>Baudrate (kbps)</FieldLabel>
            <Segmented options={BAUD_OPTIONS} value={command.baud ?? 500} onChange={() => {}} dark={dark} c={c} A={A} fluid readOnly />
          </div>
          <div style={{ minWidth: 0 }}>
            <FieldLabel c={c}>Frames in sequence</FieldLabel>
            <Segmented options={CMD_COUNT_OPTIONS} value={1} onChange={() => {}} dark={dark} c={c} A={A} fluid readOnly />
          </div>
        </div>
      </SectionCard>

      <SectionCard theme={theme}>
        <SectionHeader
          title="Frame builder"
          hint="Define each CAN frame — ARB ID, DLC, payload, and timing."
          icon={Ico.terminal}
          A={A}
          c={c}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <CommandSlotEditor
            index={0}
            slot={VIEW_FRAME_SLOT}
            onChange={() => {}}
            dark={dark}
            c={c}
            A={A}
            theme={theme}
            readOnly
          />
        </div>
      </SectionCard>

      <CommandPreviewPanel
        packet={packet}
        previewExpanded={previewExpanded}
        setPreviewExpanded={setPreviewExpanded}
        copied={copied}
        copyPreview={copyPreview}
        dark={dark}
        c={c}
        A={A}
        theme={theme}
        readOnly
      />
    </div>
  );
}

function GenericCommandViewBody({ command, dark, c, A, theme }) {
  const fields = buildCommandDetailFields(command).filter((field) => field.label !== "Command Hex");
  const packet = useMemo(() => packetFromSavedCommand(command), [command]);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  useLayoutEffect(() => {
    setPreviewExpanded(false);
    setCopied(false);
  }, [command?.id]);

  const copyPreview = async () => {
    try {
      await navigator.clipboard.writeText(packet.rawHex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionCard theme={theme}>
        <SectionHeader
          title="General"
          hint="Command metadata and configuration."
          icon={TYPE_ICONS[command.commandType] ?? Ico.commands}
          A={A}
          c={c}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
            gap: 16,
          }}
        >
          {fields.map((field) => (
            <div key={field.label} style={{ minWidth: 0 }}>
              <FieldLabel c={c}>{field.label}</FieldLabel>
              <ReadOnlyInput value={field.value} c={c} theme={theme} />
            </div>
          ))}
        </div>
      </SectionCard>

      {packet.rawHex && packet.rawHex !== "—" && (
        <CommandPreviewPanel
          packet={packet}
          previewExpanded={previewExpanded}
          setPreviewExpanded={setPreviewExpanded}
          copied={copied}
          copyPreview={copyPreview}
          dark={dark}
          c={c}
          A={A}
          theme={theme}
          readOnly
        />
      )}
    </div>
  );
}

export function CommandViewModal({ open, command, onClose, onDelete }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildModalShellTokens(dark, c, A), [dark, c, A]);
  const detailAp = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const theme = useMemo(() => buildCommandTheme(dark, c, A), [dark, c, A]);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  if (!open || !command) return null;

  const isCan = command.commandType === "can";
  const typeIcon = isCan ? Ico.chip : (TYPE_ICONS[command.commandType] ?? Ico.commands);
  const subtitle = isCan
    ? "Configure CAN bus frames and preview the command packet."
    : getCommandTypeLabel(command.commandType);

  const handleDelete = () => {
    requestDelete({
      title: "Delete command",
      description: "This command will be permanently removed from this asset.",
      confirmText: command.name,
      entityLabel: "Command name",
      confirmLabel: "Delete command",
      onConfirm: async () => {
        await onDelete(command.id);
        onClose();
      },
    });
  };

  const modal = (
    <>
      <div
        role="presentation"
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 70, background: ap.overlay, backdropFilter: "blur(8px)" }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="command-view-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(960px, calc(100vw - 24px))",
          height: "calc(100vh - 48px)",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          background: ap.cardGradient,
          border: `1px solid ${ap.cardBorder}`,
          borderRadius: 16,
          zIndex: 71,
          boxShadow: ap.cardShadowLg,
          overflow: "hidden",
        }}
      >
        <ModalShellHeader
          title={command.name}
          titleId="command-view-modal-title"
          subtitle={subtitle}
          icon={typeIcon}
          onClose={onClose}
          closeLabel="Close command details"
          dark={dark}
          c={c}
          A={A}
          ap={ap}
          trailing={(
            <button type="button" onClick={handleDelete} style={detailActionBtnStyle(detailAp, "danger")} aria-label="Delete command">
              {Ico.trash}
            </button>
          )}
        />

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: "16px 20px 20px" }}>
          {isCan ? (
            <CanCommandViewBody command={command} dark={dark} c={c} A={A} theme={theme} />
          ) : (
            <GenericCommandViewBody command={command} dark={dark} c={c} A={A} theme={theme} />
          )}
        </div>
      </div>

      <DeleteConfirmModal {...deleteModalProps} />
    </>
  );

  return createPortal(modal, document.body);
}
